import { Box, Flex, Grid, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import InfoBox from 'components/ui/InfoBox';
import {
    MdCheckCircle,
    MdError,
    MdInfo,
    MdMedicalServices,
    MdNotifications,
    MdScience,
    MdWarning,
} from 'react-icons/md';

const BR = '7px';

const Section = ({ label }: { label: string }) => {
    const color = useColorModeValue('gray.400', 'gray.500');
    return (
        <Text fontSize="xs" fontWeight="700" color={color} textTransform="uppercase"
            letterSpacing="0.1em" mt="40px" mb="16px">
            {label}
        </Text>
    );
};

// Z-pattern: row 1 icon left → row 2 icon right → row 3 icon left ...
const Z_ITEMS = [
    {
        variant: 'info' as const,
        title: 'Nueva cita programada',
        description: 'El paciente tiene una cita programada para mañana a las 10:00 am. Recuerda revisar el historial previo.',
        icon: <Icon as={MdNotifications} w="20px" h="20px" />,
        iconPosition: 'left' as const,
    },
    {
        variant: 'success' as const,
        title: 'Episodio creado correctamente',
        description: 'El episodio clínico fue abierto y está listo para completar la historia clínica con todos los pasos.',
        icon: <Icon as={MdCheckCircle} w="20px" h="20px" />,
        iconPosition: 'right' as const,
    },
    {
        variant: 'warning' as const,
        title: 'Tarjetero en estado inactivo',
        description: 'Este paciente tiene el tarjetero inactivo. Debes activarlo antes de abrir un nuevo episodio clínico.',
        icon: <Icon as={MdWarning} w="20px" h="20px" />,
        iconPosition: 'left' as const,
    },
    {
        variant: 'alert' as const,
        title: 'No se pudo guardar el diagnóstico',
        description: 'Ocurrió un error al guardar el diagnóstico diferencial. Verifica la conexión e inténtalo nuevamente.',
        icon: <Icon as={MdError} w="20px" h="20px" />,
        iconPosition: 'right' as const,
    },
    {
        variant: 'neutral' as const,
        title: 'Sin actividad reciente',
        description: 'Este paciente no tiene episodios registrados en los últimos 6 meses. Considera una reevaluación.',
        icon: <Icon as={MdMedicalServices} w="20px" h="20px" />,
        iconPosition: 'left' as const,
    },
];

const InfoBoxPreview = () => {
    const bg = useColorModeValue(
        'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 40%, #f0fdf4 100%)',
        'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f2720 100%)',
    );
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardBorder = useColorModeValue('gray.100', 'gray.700');
    const titleColor = useColorModeValue('gray.800', 'white');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');

    return (
        <Box minH="100vh" background={bg} p="40px">
            <Box maxW="860px" mx="auto">

                {/* Header */}
                <Box mb="4px">
                    <Text fontSize="2xl" fontWeight="800" color={titleColor}>InfoBox</Text>
                    <Text fontSize="sm" color={mutedColor} mt="4px">
                        Componente de notificación contextual. Prop <Text as="code" fontSize="xs" px="5px" py="1px" bg={cardBg} borderRadius="4px">icon</Text> + <Text as="code" fontSize="xs" px="5px" py="1px" bg={cardBg} borderRadius="4px">iconPosition</Text> disponibles.
                    </Text>
                </Box>

                {/* ── Z-pattern ────────────────────────────────────────────── */}
                <Section label="Z-pattern — icono alterna izquierda / derecha" />
                <InfoBox
                    borderRadius="7px" fontSize="sm" iconSize={20} iconAlign="center"
                    variant="info" iconPosition="left"
                    icon={<Icon as={MdInfo} w="100%" h="100%" />}
                    title="¿Desea completar el episodio clínico?"
                    description="Puede registrar los datos clínicos del episodio paso a paso: anamnesis, exploración, diagnóstico y pruebas."
                />

                {/* ── Variantes sin icono ───────────────────────────────────── */}
                <Section label="Sin icono — todas las variantes" />
                <Grid templateColumns="1fr 1fr" gap="10px">
                    <InfoBox borderRadius={BR} variant="info" fontSize="sm" title="Información"
                        description="Mensaje de tipo informativo general." />
                    <InfoBox borderRadius={BR} variant="success" fontSize="sm" title="Éxito"
                        description="Operación realizada correctamente." />
                    <InfoBox borderRadius={BR} variant="warning" fontSize="sm" title="Advertencia"
                        description="Hay algo que deberías revisar." />
                    <InfoBox borderRadius={BR} variant="alert" fontSize="sm" title="Alerta"
                        description="Ocurrió un problema que necesita atención." />
                    <InfoBox borderRadius={BR} variant="neutral" fontSize="sm" title="Neutral"
                        description="Notificación sin carga emocional." />
                </Grid>

                {/* ── Con icono, iconPosition left ─────────────────────────── */}
                <Section label="icon + iconPosition left — fontSize sm / md" />
                <Flex direction="column" gap="10px">
                    <InfoBox borderRadius={BR} variant="info" fontSize="sm" iconSize={18}
                        icon={<Icon as={MdInfo} w="100%" h="100%" />} iconPosition="left"
                        title="Información — sm" description="El paciente tiene una cita programada para mañana a las 10:00 am." />
                    <InfoBox borderRadius={BR} variant="success" fontSize="md" iconSize={20}
                        icon={<Icon as={MdCheckCircle} w="100%" h="100%" />} iconPosition="left"
                        title="Episodio creado — md" description="El episodio clínico fue abierto correctamente y está listo para ser completado." />
                    <InfoBox borderRadius={BR} variant="warning" fontSize="sm" iconSize={18}
                        icon={<Icon as={MdWarning} w="100%" h="100%" />} iconPosition="left"
                        title="Advertencia — sm" description="Este paciente tiene el tarjetero en estado inactivo." />
                    <InfoBox borderRadius={BR} variant="alert" fontSize="md" iconSize={20}
                        icon={<Icon as={MdError} w="100%" h="100%" />} iconPosition="left"
                        title="Error al guardar — md" description="No se pudo guardar el diagnóstico diferencial." />
                </Flex>

                {/* ── Con icono, iconPosition right ────────────────────────── */}
                <Section label="icon + iconPosition right" />
                <Flex direction="column" gap="10px">
                    <InfoBox borderRadius={BR} variant="info" fontSize="sm" iconSize={18}
                        icon={<Icon as={MdScience} w="100%" h="100%" />} iconPosition="right"
                        title="Pruebas solicitadas" description="Se han solicitado 3 pruebas complementarias pendientes de resultado." />
                    <InfoBox borderRadius={BR} variant="success" fontSize="sm" iconSize={18}
                        icon={<Icon as={MdCheckCircle} w="100%" h="100%" />} iconPosition="right"
                        title="Historia completada" description="Todos los pasos del episodio clínico han sido completados." />
                    <InfoBox borderRadius={BR} variant="neutral" fontSize="sm" iconSize={18}
                        icon={<Icon as={MdMedicalServices} w="100%" h="100%" />} iconPosition="right"
                        title="Sin episodios activos" description="Este paciente no tiene episodios abiertos o en tratamiento actualmente." />
                </Flex>

                {/* ── iconAlign ────────────────────────────────────────────── */}
                <Section label="iconAlign — top / center / bottom" />
                <Flex direction="column" gap="10px">
                    <InfoBox borderRadius={BR} variant="info" fontSize="sm" iconAlign="top"
                        icon={<Icon as={MdInfo} w="20px" h="20px" />} iconPosition="left"
                        title="iconAlign top (default)"
                        description={"El icono se alinea con la parte superior del contenido de texto.\nÚtil cuando la descripción es larga y quieres que el icono marque el inicio del bloque."} />
                    <InfoBox borderRadius={BR} variant="success" fontSize="sm" iconAlign="center"
                        icon={<Icon as={MdCheckCircle} w="20px" h="20px" />} iconPosition="left"
                        title="iconAlign center"
                        description={"El icono se centra verticalmente respecto al bloque de texto completo.\nIdeal para mensajes cortos de una o dos líneas."} />
                    <InfoBox borderRadius={BR} variant="warning" fontSize="sm" iconAlign="bottom"
                        icon={<Icon as={MdWarning} w="20px" h="20px" />} iconPosition="left"
                        title="iconAlign bottom"
                        description={"El icono se alinea con la parte inferior del contenido.\nCaso de uso poco frecuente, útil para layouts decorativos o ancla visual al final del bloque."} />
                </Flex>

                {/* ── iconSize ─────────────────────────────────────────────── */}
                <Section label="iconSize — 16 / 20 / 28 / 40px" />
                <Grid templateColumns="1fr 1fr" gap="10px">
                    <InfoBox borderRadius={BR} variant="info" fontSize="sm" iconSize={16}
                        icon={<Icon as={MdInfo} w="100%" h="100%" />} iconPosition="left"
                        title="iconSize 16" description="Icono pequeño para layouts densos o listas compactas." />
                    <InfoBox borderRadius={BR} variant="success" fontSize="sm" iconSize={20}
                        icon={<Icon as={MdCheckCircle} w="100%" h="100%" />} iconPosition="left"
                        title="iconSize 20 (default)" description="Tamaño estándar. Equilibrio entre presencia y densidad." />
                    <InfoBox borderRadius={BR} variant="warning" fontSize="sm" iconSize={28}
                        icon={<Icon as={MdWarning} w="100%" h="100%" />} iconPosition="left"
                        title="iconSize 28" description="Icono prominente para mensajes que requieren más atención visual." />
                    <InfoBox borderRadius={BR} variant="alert" fontSize="sm" iconSize={40}
                        icon={<Icon as={MdError} w="100%" h="100%" />} iconPosition="left"
                        padding="20px" iconAlign="center"
                        title="iconSize 40" description="Icono grande para banners de error crítico o pantallas vacías." />
                </Grid>

                {/* ── Contexto clínico real ─────────────────────────────────── */}
                <Section label="Contexto — dentro de modal / formulario" />
                <Box bg={cardBg} borderRadius="12px" p="24px" border="1px solid" borderColor={cardBorder}>
                    <Text fontSize="sm" fontWeight="700" color={titleColor} mb="16px">
                        Wizard nuevo episodio
                    </Text>
                    <Flex direction="column" gap="10px">
                        <InfoBox borderRadius={BR} variant="info" fontSize="sm"
                            icon={<Icon as={MdInfo} w="18px" h="18px" />} iconPosition="left"
                            title="¿Desea completar la historia clínica?"
                            description="Puede registrar anamnesis, exploración física, diagnóstico diferencial, pruebas complementarias y diagnóstico final." />
                        <InfoBox borderRadius={BR} variant="warning" fontSize="sm"
                            icon={<Icon as={MdWarning} w="18px" h="18px" />} iconPosition="left"
                            title="Paso opcional"
                            description="Registra la impresión diagnóstica inicial. Puedes completarla desde la historia clínica cuando quieras." />
                        <InfoBox borderRadius={BR} variant="success" fontSize="sm"
                            icon={<Icon as={MdCheckCircle} w="18px" h="18px" />} iconPosition="left"
                            title="Episodio en tratamiento"
                            description="Al guardar la exploración física, el episodio pasa automáticamente a estado 'En tratamiento'." />
                        <InfoBox borderRadius={BR} variant="alert" fontSize="sm"
                            icon={<Icon as={MdError} w="18px" h="18px" />} iconPosition="left"
                            title="Episodio cerrado"
                            description="Este episodio está cerrado y no acepta nuevas notas SOAP. Reabre el episodio para continuar." />
                    </Flex>
                </Box>

            </Box>
        </Box>
    );
};

export default InfoBoxPreview;
